import React from "react";
import { IconButton } from "@mui/material";
import dayjs from "dayjs";
import { StatusBadge } from "./status-badge";
import { SatisfactionIcon } from "./satisfaction-icon";
import { useTranslation } from "react-i18next";
import { ENABLE_SAT_COLUMN } from "../sr-constants";

interface ServiceRequestCardProps {
    data: any;
    onEdit: () => void;
    onDelete: () => void;
    onClick: () => void;
    onRequestsClick?: (e: React.MouseEvent) => void;
    isMyRequests?: boolean;
}

export const ServiceRequestCard: React.FC<ServiceRequestCardProps> = ({
    data,
    onEdit,
    onDelete,
    onClick,
    onRequestsClick,
    isMyRequests = false,
}) => {
    const { t } = useTranslation("service_request_home");

    const created = dayjs(data.created);
    const updated = dayjs(data.modified);
    const daysAgo = dayjs().diff(updated, "day");

    return (
        <div
            className="group relative cursor-pointer overflow-hidden rounded-xl border border-gray-200 bg-white shadow-md transition-all duration-300 hover:border-gray-300 hover:shadow-xl"
            onClick={onClick}
        >
            {/* ============================================================
                TOP SECTION
            ============================================================ */}

            <div className="flex items-center justify-between p-4 pb-0">
                <StatusBadge status={data?.status?.status} size="large" />

                {ENABLE_SAT_COLUMN && (
                    <div className="flex items-center">
                        <SatisfactionIcon requestId={data.id} size="medium" />
                    </div>
                )}
            </div>

            {/* ============================================================
                CARD CONTENT
            ============================================================ */}

            <div className="space-y-3 p-4">
                {/* Tracking ID */}
                <div className="flex items-center justify-between">
                    <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                        {t("srtable_trackingid")}
                    </span>

                    <span className="text-sm font-bold text-gray-900">{data.trackingid}</span>
                </div>

                {/* ========================================================
                    SUBJECT / BENEFICIARY
                ======================================================== */}

                <div className="group/beneficiary relative">
                    <div className="flex items-center justify-between gap-2">
                        {/* Name + Phone */}
                        <div className="min-w-0 flex-1">
                            <h3 className="line-clamp-1 text-lg font-semibold text-gray-900">
                                {data?.subject?.subject?.profile?.name_eng || "N/A"}
                            </h3>

                            <p className="mt-1 text-sm text-gray-600">
                                {data?.subject?.subject?.whatsapp || "No phone"}
                            </p>
                        </div>

                        {/* =================================================
                            REQUESTS BUTTON
                            ================================================= */}

                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();

                                onRequestsClick?.(e);
                            }}
                            className="flex shrink-0 items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-2.5 py-1.5 text-xs font-semibold text-blue-600 opacity-0 shadow-sm transition-all duration-200 hover:border-blue-300 hover:bg-blue-100 hover:text-blue-700 group-hover:opacity-100"
                            title="View all requests"
                        >
                            <i className="fas fa-list-check text-[11px]" />

                            <span>Requests</span>
                        </button>
                    </div>
                </div>

                {/* ========================================================
                    REQUEST TYPE
                ======================================================== */}

                <div className="flex items-start space-x-2">
                    <i className="fas fa-clipboard-list mt-1 text-gray-400" />

                    <div className="min-w-0 flex-1">
                        <p className="text-xs text-gray-500">{t("srtable_request")}</p>

                        <p className="line-clamp-2 text-sm font-medium text-gray-800">
                            {data?.type?.type?.name || "N/A"}
                        </p>
                    </div>
                </div>

                {/* ========================================================
                    ASSIGNEE
                ======================================================== */}

                <div className="flex items-center space-x-2">
                    <i className="fas fa-user-circle text-gray-400" />

                    <div className="min-w-0 flex-1">
                        <p className="text-xs text-gray-500">{t("srtable_assigned")}</p>

                        <p className="truncate text-sm font-medium text-gray-800">
                            {data?.assignee?.is_office_person
                                ? data?.assignee?.user_profile?.name
                                : data?.assignee?.assignee?.name || "Unassigned"}
                        </p>
                    </div>
                </div>

                {/* ========================================================
                    DATE INFO
                ======================================================== */}

                <div className="flex items-center space-x-2">
                    <i className="fas fa-calendar-alt text-gray-400" />

                    <div className="min-w-0 flex-1">
                        <p className="text-xs text-gray-500">{created.format("MMM D, YYYY")}</p>

                        <p className="text-xs text-gray-400">
                            {t("date_update")} {daysAgo} {t("days")}
                        </p>
                    </div>
                </div>
            </div>

            {/* ============================================================
                EDIT / DELETE ACTIONS
            ============================================================ */}

            <div className="absolute right-2 top-2 flex space-x-1 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                {/* Edit */}
                <IconButton
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        onEdit();
                    }}
                    className="bg-white shadow-md hover:bg-blue-50"
                    sx={{
                        backgroundColor: "white",
                        "&:hover": {
                            backgroundColor: "#EFF6FF",
                        },
                    }}
                >
                    <i className="fas fa-edit text-sm text-blue-600" />
                </IconButton>

                {/* Delete */}
                <IconButton
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete();
                    }}
                    className="bg-white shadow-md hover:bg-red-50"
                    sx={{
                        backgroundColor: "white",
                        "&:hover": {
                            backgroundColor: "#FEF2F2",
                        },
                    }}
                >
                    <i className="fas fa-trash text-sm text-red-600" />
                </IconButton>
            </div>
        </div>
    );
};
